import { useEffect } from 'react';
import { usePage } from '@inertiajs/react';

import { resetTagCatalogCache } from '@/components/page-sections/metadata-controls/use-tag-catalog';
import { disconnectCable } from '@/lib/cable';
import { useAccountLimitsStore } from '@/stores/account-limits-store';
import { usePagesStore } from '@/stores/pages-store';
import { usePlanLimitModalStore } from '@/stores/plan-limit-modal-store';
import { useResourcesStore } from '@/stores/resources-store';
import { useSavedSectionsStore } from '@/stores/saved-sections-store';
import type { SharedData } from '@/types/shared';

type SharedSessionData = Partial<Pick<SharedData, 'accountId' | 'currentUser'>>;

// Module-scoped so logout/login layout swaps still compare against the previous session.
let lastSessionScopeKey: string | undefined;

function resetAccountScopedClientState() {
  resetTagCatalogCache();
  useAccountLimitsStore.getState().reset();
  usePagesStore.getState().reset();
  useResourcesStore.getState().reset();
  useSavedSectionsStore.getState().reset();
  usePlanLimitModalStore.getState().reset();
}

/**
 * Detects authenticated session scope changes and resets account-scoped client state.
 *
 * This handles the multi-tab scenario where:
 * 1. Tab A has Account 1's data loaded in stores
 * 2. Tab B switches to Account 2 (full page reload in Tab B)
 * 3. Tab A navigates via Inertia - server sends Account 2's accountId
 * 4. This hook detects the mismatch and resets stores and realtime connections
 *
 */
export function useAccountChangeReset(): void {
  const { accountId, currentUser } = usePage<SharedSessionData>().props;

  useEffect(() => {
    const currentSessionScopeKey = `${currentUser?.id ?? 'anonymous'}:${accountId ?? 'no-account'}`;

    if (lastSessionScopeKey === undefined) {
      lastSessionScopeKey = currentSessionScopeKey;
      return;
    }

    if (lastSessionScopeKey !== currentSessionScopeKey) {
      resetAccountScopedClientState();
      disconnectCable();
      lastSessionScopeKey = currentSessionScopeKey;
      return;
    }
  }, [accountId, currentUser?.id]);
}
